import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { colorPalette, colorStrings, mediaQueriesBP } from "@solveindustrial/Utilities/variables";
import * as cssLinter from "css";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    columns = "columns",
    gap = "gap",
    topColor = "topColor",
    bottomColor = "bottomColor",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.columns]: GridWidths[];
        [fields.gap]: number;
        [fields.topColor]: string;
        [fields.bottomColor]: string;
        [fields.customCSS]: string;
    };
    extendedStyles?: FooterContainerStyles;
}

export const mapStateToProps = (_: ApplicationState, __: OwnProps) => {
    return {};
};

export type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface FooterContainerStyles {
    widgetGridContainer?: GridContainerProps;
    logoGridItem?: GridItemProps;
    logoWrapper?: InjectableCss;
    emptyGridItem?: GridItemProps;
    listGridsItem?: GridItemProps;
    bottomGridItem?: GridItemProps;
    bottomWrapper?: InjectableCss;
}

const defaultCustomCss = `
.footer-container__bottom {
}
`;

export const footerContainerStyles: FooterContainerStyles = {
    widgetGridContainer: {
        gap: 0,
        css: css`
            padding: 43px 15px 0;

            &::before,
            &::after {
                content: "";
                background-color: ${getColor(colorStrings.commonGlobalWhite)};
                position: absolute;
                top: 0;
                bottom: 0;
                z-index: 0;
            }

            &::before {
                left: calc(((100vw - 100%) / 2) * -1);
                right: calc(100% - 5px);
            }

            &::after {
                left: calc(100% - 5px);
                right: calc(((100vw - 100%) / 2) * -1);
            }
        `,
    },
    logoGridItem: {
        width: [12, 12, 6, 3, 3],
        css: css`
            width: 100%;
            display: flex;
            flex-direction: column;
            justify-content: flex-start;

            img {
                height: 78px;
            }
        `,
    },
    logoWrapper: {
        css: css`
            ${mediaQueriesBP.maxMD} {
                text-align: center;
                width: 100%;
            }

            ${mediaQueriesBP.minMD} {
                width: calc(100% - 20px);
            }
        `,
    },
    emptyGridItem: { width: [0, 0, 0, 1, 1] },
    listGridsItem: { width: [12, 12, 6, 8, 8] },
    bottomGridItem: {
        width: 12,
        css: css`
            position: relative;

            &&& {
                margin-top: 40px;
            }
        `,
    },
    bottomWrapper: {
        css: css`
            background-color: ${getColor(colorStrings.primaryMain)};
            width: 100%;
            z-index: 1;

            &::before,
            &::after {
                content: "";
                background-color: ${getColor(colorStrings.primaryMain)};
                position: absolute;
                top: 0;
                bottom: 0;
                z-index: -1;
            }

            &::before {
                left: calc(((100vw - 100%) / 2) * -1);
                right: calc(100% - 5px);
            }

            &::after {
                left: calc(100% - 5px);
                right: calc(((100vw - 100%) / 2) * -1);
            }

            & > [class*="Wrapper-sc"] {
                display: flex;
                position: relative;
                z-index: 1;

                ${mediaQueriesBP.minMD} {
                    justify-content: space-between;
                    align-items: center;
                }

                ${mediaQueriesBP.maxMD} {
                    flex-direction: column;
                    justify-content: center;
                    align-items: flex-start;
                }
            }

            ${mediaQueriesBP.maxMD} {
                padding: 20px 0;
            }
        `,
    },
};

export const styles = footerContainerStyles;

const mobileColumnsRef: { [key: number]: GridWidths | GridWidths[] } = {
    1: [3, 3, 1, 1, 1],
    2: [4, 4, 2, 2, 2],
    3: [6, 6, 3, 3, 3],
    4: [6, 6, 4, 4, 4],
    5: [6, 6, 5, 5, 5],
    6: [6, 6, 6, 6, 6],
    7: [12, 12, 7, 7, 7],
    8: [12, 12, 8, 8, 8],
    9: [12, 12, 9, 9, 9],
    10: [12, 12, 10, 10, 10],
    11: [12, 12, 11, 11, 11],
    12: 12,
};

const convertToResponsive = (column: GridWidths): GridWidths | GridWidths[] => {
    return mobileColumnsRef[column] || column;
};

const FooterContainer: FC<OwnProps> = ({ id, extendedStyles, ...props }) => {
    const [styles] = React.useState(() => mergeToNew(footerContainerStyles, extendedStyles));

    const buildListGrids = () => {
        const {
            fields: { columns, gap },
        } = props;

        const columnElements: JSX.Element[] = [];

        for (let x = 0; x < columns.length; x += 1) {
            columnElements.push(
                <GridItem key={x} width={convertToResponsive(columns[x])}>
                    <Zone contentId={id as never} zoneName={`FooterContentLinks${x}` as never} />
                </GridItem>,
            );
        }

        return <GridContainer gap={gap || 0}>{columnElements}</GridContainer>;
    };

    const customCssWrapper = {
        css: css`
            ${props.fields.customCSS}
        `,
    };

    const topBackgroundOverride = `
        background-color: ${props.fields.topColor};

        &::before,
        &::after {
            background-color: ${props.fields.topColor};
        }`;
    const widgetGridContainer = {
        ...styles.widgetGridContainer,
        css: css`
            ${styles.widgetGridContainer?.css}
            ${props.fields.topColor ? topBackgroundOverride : ""}
        `,
    };

    const bottomBackgroundOverride = `
        background-color: ${props.fields.bottomColor};

        &::before,
        &::after {
            background-color: ${props.fields.bottomColor};
        }`;
    const bottomWrapper = {
        ...styles.bottomWrapper,
        css: css`
            ${styles.bottomWrapper?.css}
            ${props.fields.bottomColor ? bottomBackgroundOverride : ""}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <GridContainer {...widgetGridContainer}>
                <GridItem {...styles.logoGridItem}>
                    <StyledWrapper {...styles.logoWrapper}>
                        <Zone zoneName={"Logo" as never} contentId={id as never} />
                    </StyledWrapper>
                </GridItem>
                <GridItem {...styles.emptyGridItem}></GridItem>
                <GridItem {...styles.listGridsItem}>{buildListGrids()}</GridItem>
                <GridItem {...styles.bottomGridItem}>
                    <StyledWrapper {...bottomWrapper} className="footer-container__bottom">
                        <Zone zoneName={"Bottom" as never} contentId={id as never} />
                    </StyledWrapper>
                </GridItem>
            </GridContainer>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const footerContainer: WidgetModule = {
    component: FooterContainer,
    definition: {
        displayName: "Footer Container",
        group: "Verndale Box - Footer" as any,
        icon: "question",
        allowedContexts: ["Footer"],
        fieldDefinitions: [
            {
                name: fields.columns,
                displayName: "Layout",
                editorTemplate: "ColumnsField",
                defaultValue: [3, 3, 3, 3],
                fieldType: "General",
                sortOrder: 1,
                validate: value => (value.length > 0 ? null : "Please enter a valid layout."),
                tab: basicTab,
            },
            {
                name: fields.gap,
                displayName: "Gap Between Items",
                editorTemplate: "IntegerField",
                defaultValue: 20,
                fieldType: "General",
                max: 1000,
                min: 0,
                sortOrder: 2,
                tab: basicTab,
            },
            {
                name: fields.topColor,
                displayName: "Top bar background",
                editorTemplate: "ColorPickerField",
                defaultValue: "#FFFFFF",
                fieldType: "General",
                sortOrder: 3,
                tab: basicTab,
            },
            {
                name: fields.bottomColor,
                displayName: "Bottom bar background",
                editorTemplate: "ColorPickerField",
                defaultValue: colorPalette.primary?.main ?? "#FFFFFF",
                fieldType: "General",
                sortOrder: 4,
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default footerContainer;
